import { useAtom } from 'jotai';
import {
  alpha,
  Box,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import UploadCard from '@components/DashboardShell/Uploads/UploadCard';
import {
  UploadCardsContainer,
  UploadSidebar,
} from '@components/DashboardShell/Uploads/StyledComponents';
import { uploadsAtom } from '@/atoms/uploadsAtom';

interface UploadsSidebarProps {
  onClose: () => void;
}

const UploadsSidebar = ({ onClose }: UploadsSidebarProps) => {
  const [uploads] = useAtom(uploadsAtom);
  const uploadCount = uploads.length;

  return (
    <UploadSidebar>
      <Paper
        component="header"
        elevation={0}
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.5,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          borderRadius: 0,
          flexShrink: 0,
        }}
      >
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <CloudUploadOutlinedIcon />
            <Box>
              <Typography variant="subtitle1">Upload tray</Typography>
              <Typography variant="caption" sx={{ opacity: 0.82 }}>
                {`${uploadCount} active upload${uploadCount === 1 ? '' : 's'}`}
              </Typography>
            </Box>
          </Stack>
          <Tooltip title="Close upload tray">
            <IconButton
              onClick={onClose}
              aria-label="close upload tray"
              size="small"
              sx={(theme) => ({
                color: 'inherit',
                border: `1px solid ${alpha(theme.palette.common.white, 0.24)}`,
                '&:hover': {
                  bgcolor: alpha(theme.palette.common.white, 0.12),
                },
              })}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>
      {uploadCount === 0 ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={0.75}
          sx={{ flex: 1, minHeight: 0, px: 3, py: 5 }}
        >
          <CloudUploadOutlinedIcon color="disabled" fontSize="large" />
          <Typography variant="subtitle1">No uploads in progress</Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            Upload files from the File Manager to monitor them here.
          </Typography>
        </Stack>
      ) : (
        <UploadCardsContainer>
          {uploads.map((upload) => (
            <UploadCard
              key={upload.id}
              id={upload.id}
              location={upload.location}
            />
          ))}
        </UploadCardsContainer>
      )}
    </UploadSidebar>
  );
};

export default UploadsSidebar;
